import { useCallback, useState } from "react";
import { useTranslation } from "react-i18next";
import { RsvpDialog } from "../rsvp";
import { toShortCode } from "../../hooks/useLanguage";
import { formatDeadline } from "../../invitation/rsvpDeadline";

// ─── Component ─────────────────────────────────────────────────

/**
 * CtaSection
 *
 * Renders the "Accept our invitation" CTA anchor. When `isRsvp` is
 * true, clicking the anchor opens the RSVP modal (RsvpDialog). When
 * `isRsvp` is false, the anchor behaves as a plain external link.
 *
 * The CTA markup itself is untouched — the classes `ball cta`,
 * `cta-black-arc`, `cta-im-wrap`, `cta-link`, `cta-txt`, and
 * `mbm-exclusion` are all preserved to keep the existing Webflow
 * styling + hover animations intact.
 *
 * Props:
 *   id         — section id
 *   text       — CTA label (default: "Accept our invitation")
 *   href       — target URL when isRsvp is false
 *   isRsvp     — toggle RSVP modal mode
 *   rsvpCode   — personal code; when present the modal opens directly
 *                in the coded flow rather than the choice screen
 *   eventId    — required when isRsvp is true
 *   eventType  — celebration type, forwarded to RSVP analytics (rsvp_started)
 *   ...rest    — extra attributes for the <section> (the editor's preview
 *                marks it to find it)
 */

// Strip everything except digits and a leading "+" so the value is safe for
// the `tel:` URI scheme. Stored values look like "(+30) 6912345678".
const toTelHref = (phone) => {
  if (!phone) return "";
  const cleaned = phone.replace(/[^\d+]/g, "");
  return cleaned ? `tel:${cleaned}` : "";
};

// Phones are stored as "(+30) 6912345678". When a couple opens the phone
// field and picks a country code but never types a number, we save the bare
// "(+30) " — render that as "no phone" rather than a useless dial-code line.
const hasDialableNumber = (phone) => {
  if (!phone) return false;
  const body = phone.replace(/\([^)]*\)/g, ""); // drop the "(+30)" prefix
  return /\d/.test(body);
};

const CtaSection = ({
  id,
  text,
  href = null,
  isRsvp = false,
  rsvpCode = null,
  eventId = null,
  eventType = null,
  rsvpDeadline = null,
  contacts = [],
  ...rest
}) => {
  const { t, i18n } = useTranslation();
  const [modalOpen, setModalOpen] = useState(false);
  const ctaText = text || t("home.cta.buttonText");
  const localeTag = toShortCode(i18n.resolvedLanguage || i18n.language) === "el" ? "el-GR" : "en-US";

  const handleClick = useCallback(
    (e) => {
      if (!isRsvp) return; // external link — let the anchor navigate
      e.preventDefault();
      setModalOpen(true);
    },
    [isRsvp],
  );

  const deadlineLabel = isRsvp ? formatDeadline(rsvpDeadline, localeTag) : "";
  // Show one row per partner who has a phone configured. Couples often only
  // fill one — by design we render whichever are present.
  const visibleContacts = isRsvp
    ? (contacts || []).filter((c) => c && hasDialableNumber(c.phone))
    : [];
  const hasDetails = Boolean(deadlineLabel) || visibleContacts.length > 0;

  return (
    <section
      id={id}
      data-w-id="69357659-14a0-5526-745f-0888c5cbe668"
      className="section cta"
      {...rest}
    >
      <div className="container cta">
        <div className="cta-im-wrap">
          <div className="cta-black-arc"></div>
        </div>
        <a
          href={isRsvp ? "#rsvp-form" : href}
          className="cta-link mbm-exclusion w-inline-block"
          onClick={handleClick}
          target={isRsvp ? undefined : "_blank"}
          rel={isRsvp ? undefined : "noopener noreferrer"}
        >
          <div className="ball cta mbm-exclusion"></div>
          <div className="cta-txt mbm-exclusion">{ctaText}</div>
        </a>
        {/* Deadline + contact details render in normal flow below the CTA, in
            dark ink on the cream container so they stay readable on every
            breakpoint (the arc behind the CTA is hidden on mobile, which made
            the previous white, absolutely-positioned overlay disappear). */}
        {hasDetails && (
          <div
            className="cta-details"
            style={{
              position: "relative",
              zIndex: 5,
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              gap: "1rem",
              marginTop: "1.5rem",
              padding: "0 1.25rem",
              maxWidth: "32rem",
              textAlign: "center",
              color: "var(--black)",
            }}
          >
            {deadlineLabel && (
              <p
                className="signature cta-deadline"
                style={{
                  margin: 0,
                  letterSpacing: "1.5px",
                }}
              >
                {t("home.cta.deadline", { date: deadlineLabel })}
              </p>
            )}
            {visibleContacts.length > 0 && (
              <div
                className="cta-contacts"
                style={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  gap: "0.4rem",
                  fontSize: "0.82rem",
                  letterSpacing: "1px",
                }}
              >
                <span
                  className="cta-contacts-label"
                  style={{
                    textTransform: "uppercase",
                    opacity: 0.6,
                  }}
                >
                  {t("home.cta.contactsLabel")}
                </span>
                {visibleContacts.map((c, idx) => {
                  const tel = toTelHref(c.phone);
                  const label = c.name ? `${c.name} — ${c.phone}` : c.phone;
                  return tel ? (
                    <a
                      key={idx}
                      href={tel}
                      style={{
                        color: "var(--black)",
                        textDecoration: "none",
                        borderBottom: "1px solid rgba(0,0,0,0.25)",
                        paddingBottom: "1px",
                      }}
                    >
                      {label}
                    </a>
                  ) : (
                    <span key={idx}>{label}</span>
                  );
                })}
              </div>
            )}
          </div>
        )}
      </div>
      {isRsvp && (
        <RsvpDialog
          open={modalOpen}
          onClose={() => setModalOpen(false)}
          eventId={eventId}
          rsvpCode={rsvpCode}
          eventType={eventType}
        />
      )}
    </section>
  );
};

export default CtaSection;
